import { throttle } from 'lodash';
import styled from '@emotion/styled';
import { Container, Fade, Grid, lighten, Tooltip } from '@mui/material';
import Cell from './Cell';
import React, { createRef, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectCurrentTable,
  selectCurrentTableKey,
  selectDataTablePage,
  selectDataTableParams,
  selectDataTableSettings,
  selectDataTableSorting,
  selectIsFiltered,
  selectUserColumns
} from '@/redux/selectors/dataTableSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { ColumnConfig } from '@/__configs/config_types';
import { clearFilters, collapseAllRows, getData, setCurrentPage, setPageSize, setSorting } from '@/redux/slices/dataTableSlice';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '../../ErrorFallback';
import { LoadingStatuses } from '@/constants/constants';
import { selectAuth, selectUserPreferences } from '@/redux/selectors/authSelectors';
import TableNoResults from '../TableNoResults';
import TablePagination from '../TableFooter/TablePagination';
import TablePageSize from '../TableFooter/TablePageSize';
import { savePreferences, updatePageSize } from '@/redux/slices/authSlice';
import { ScrollPosition } from './PolarisTableContainer.types';
import TableRow from './TableRow';
import TableFooter from '../TableFooter/TableFooter';
import TableCounts from '../TableFooter/TableCounts';
import { useHistory } from 'react-router-dom';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import PolarisIcon from '../../PolarisIcon';
import { getNextSortOrder, SortOrder } from '../sortOrders';
import LoadingBar from '../../LoadingBar';
import useResizeObserver from '@/components/_hooks/useResizeObserver';
import { IngestFile } from '@/redux/slices/slice_types';
import { ROW_HEIGHT } from '@/components/_common/Table/PolarisTableContainer/constants';

const DRAWER_ANIMATION_TIMING = 400;
const SCROLLABLE_CELL_MIN_WIDTH = 110;

interface PolarisTableContainerProps {
  simplified?: boolean;
  onRowClick?: (id: any, item?: any) => void | undefined;
}

export type ColumnWidth = {
  flex?: number;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
};

export type ColumnWidths = ColumnWidth[];

const calculateGridTemplateColumns = (
  widths: ColumnWidths,
  addIconRoom: boolean,
  position: 'left' | 'right' | 'scrollable',
  frWidth: number | null
) => {
  const allColumnsHasMaxWidth = widths.every((col) => col.maxWidth);
  return (
    (addIconRoom ? '40px ' : '') +
    `${widths
      .map(({ width, minWidth, maxWidth, flex }, currIndex) => {
        if (position === 'scrollable' && frWidth) {
          if (frWidth > SCROLLABLE_CELL_MIN_WIDTH) {
            if (flex) return `${flex}fr`;
            if (currIndex === widths.length - 1 && maxWidth && allColumnsHasMaxWidth) {
              const maxWidthSum = widths.reduce((acc, col) => acc + (col.maxWidth || 0), 0);
              if (maxWidthSum < frWidth) return `minmax(${width || minWidth}px,1fr)`;
            }
            if (width || minWidth || maxWidth) {
              return `minmax(${width || minWidth || SCROLLABLE_CELL_MIN_WIDTH}px, ${width || maxWidth ? `${width || maxWidth}px` : '1fr'})`;
            } else return '1fr';
          } else {
            if (width || minWidth || maxWidth) {
              return `minmax(${width || minWidth || 0}px,${width || maxWidth ? `${width || maxWidth}px` : 'auto'})`;
            }
            return flex ? `minmax(${(flex - 1) * 100}px, auto)` : `${SCROLLABLE_CELL_MIN_WIDTH}px`;
          }
        }

        if (width) return `${width}px`;
        if (minWidth || maxWidth) return `minmax(${minWidth || 0}px,${maxWidth || 'auto'})`;
        return `${SCROLLABLE_CELL_MIN_WIDTH}px`;
      })
      .join(' ')}`
  );
};

export type ExpansionState = {
  id: string | number;
  currentDrawerHeight: number;
  drawerRef: RefObject<HTMLDivElement>;
};

type TableLayout = {
  headers: ColumnConfig<any, any>[];
  widths: ColumnWidth[];
  gridTemplate: string;
};

export type TableData = {
  expandableRender: null | ((row: IngestFile, frozenRightWidth: number) => React.ReactNode);
  tables: {
    left: TableLayout;
    scrollable: TableLayout;
    right: TableLayout;
  };
};

const PolarisTableContainer = ({ simplified, onRowClick }: PolarisTableContainerProps) => {
  const dispatch = useAppDispatch();
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const rightTableRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableCols = useAppSelector(selectUserColumns);
  const cols = tableCols.filter((col) => col.id !== 'expandable' && col.id !== 'overflow');
  const { data, status, pagination } = useAppSelector(selectCurrentTable);
  const tableSettings = useAppSelector(selectDataTableSettings);
  const tableParams = useAppSelector(selectDataTableParams);
  const { status: profileStatus } = useAppSelector(selectAuth);
  const isFiltered = useAppSelector(selectIsFiltered);
  const loading = useMemo(() => status === LoadingStatuses.pending || profileStatus === LoadingStatuses.pending, [status, profileStatus]);
  const tableKey = useAppSelector(selectCurrentTableKey);
  const currentPage = useAppSelector(selectDataTablePage);
  const [expansionState, setExpansionState] = useState<ExpansionState[]>([]);
  const [expandedRows, setExpandedRows] = useState<(string | number)[]>([]);
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(null);
  const { order, id } = useAppSelector(selectDataTableSorting);
  const gridTemplateColumnsCachedBreakpoints = useRef<{
    left: string | null;
    scrollable: { belowMinWidth: string | null; aboveEqualToMinWidth: string | null };
    right: string | null;
  }>({
    left: null,
    scrollable: { belowMinWidth: null, aboveEqualToMinWidth: null },
    right: null
  });
  const userPreferences = useAppSelector(selectUserPreferences);
  const history = useHistory();
  const preferencesGridViewEnabled = useMemo(() => {
    return userPreferences[tableKey as keyof typeof userPreferences]?.grid || false;
  }, [userPreferences, tableKey]);
  const { gridItemRender: clientGridItemRender, enableGridViewToggle: clientEnableGridViewToggle } = useConfigValue(['dataConfig'])[tableKey as any];
  const [tableData, setTableData] = useState<TableData>({
    expandableRender: null,
    tables: {
      left: {
        gridTemplate: '',
        headers: [],
        widths: []
      },
      scrollable: {
        gridTemplate: '',
        headers: [],
        widths: []
      },
      right: {
        gridTemplate: '',
        headers: [],
        widths: []
      }
    }
  });

  const resetCache = () => {
    gridTemplateColumnsCachedBreakpoints.current = {
      left: null,
      scrollable: { belowMinWidth: null, aboveEqualToMinWidth: null },
      right: null
    };
  };

  useEffect(() => {
    // if tableCols changes, reset cache
    resetCache();

    // logic
    const builtTableData: TableData = {
      expandableRender: null,
      tables: {
        left: {
          gridTemplate: '',
          headers: [],
          widths: []
        },
        scrollable: {
          gridTemplate: '',
          headers: [],
          widths: []
        },
        right: {
          gridTemplate: '',
          headers: [],
          widths: []
        }
      }
    };

    // iterate through the tableColumns ONCE
    for (const column of tableCols) {
      const isExpandable = column?.isExpandable || column?.id === 'expandable';
      const isOverflow = column?.id === 'overflow';

      const subTable: keyof TableData['tables'] = isExpandable ? 'left' : isOverflow ? 'right' : column?.position || 'scrollable';

      if (isExpandable) {
        builtTableData.expandableRender = column?.render;
      }

      if (!column?.hidden) {
        const width = {
          flex: column?.flex,
          width: column?.width,
          minWidth: column?.minWidth,
          maxWidth: column?.maxWidth
        };

        builtTableData.tables[subTable].headers.push(column);
        if (!isExpandable) builtTableData.tables[subTable].widths.push(width);
      }
    }

    // calculate gridTemplateColums for each table
    const scrollRef = scrollableRef.current;
    const containerRef = tableContainerRef.current;
    if (scrollRef && containerRef) {
      const cachedData = gridTemplateColumnsCachedBreakpoints.current;
      const fractionalWidth = {
        left: scrollRef.clientWidth / builtTableData.tables.left.headers.length,
        scrollable: scrollRef.clientWidth / builtTableData.tables.scrollable.headers.length,
        right: scrollRef.clientWidth / builtTableData.tables.right.headers.length
      };

      // left gridtemplate
      builtTableData.tables.left.gridTemplate =
        cachedData.left ||
        calculateGridTemplateColumns(builtTableData.tables.left.widths, !!builtTableData.expandableRender, 'left', fractionalWidth.left);

      // right gridtemplate
      builtTableData.tables.right.gridTemplate =
        cachedData.right || calculateGridTemplateColumns(builtTableData.tables.right.widths, false, 'right', fractionalWidth.right);

      // scrollable gridtemplate
      builtTableData.tables.scrollable.gridTemplate =
        cachedData.scrollable.belowMinWidth && fractionalWidth.scrollable < SCROLLABLE_CELL_MIN_WIDTH
          ? cachedData.scrollable.belowMinWidth
          : cachedData.scrollable.aboveEqualToMinWidth && fractionalWidth.scrollable >= SCROLLABLE_CELL_MIN_WIDTH
            ? cachedData.scrollable.aboveEqualToMinWidth
            : calculateGridTemplateColumns(builtTableData.tables.scrollable.widths, false, 'scrollable', fractionalWidth.scrollable);
    }

    setTableData(builtTableData);
  }, [tableCols, scrollableRef, tableContainerRef]);

  const updateGridTemplateColumns = () => {
    // calculate grid-template-columns for each table
    if (scrollableRef.current && tableContainerRef.current) {
      const fractionalWidth = scrollableRef.current.clientWidth / tableData.tables.scrollable.headers.length;

      // if no grid-template-columns calculation has been done and stashed for this breakpoint yet, calculate it
      // otherwise, use the stashed value

      let scrollableValue = '';
      if (gridTemplateColumnsCachedBreakpoints.current.scrollable.belowMinWidth && fractionalWidth < SCROLLABLE_CELL_MIN_WIDTH) {
        scrollableValue = gridTemplateColumnsCachedBreakpoints.current.scrollable.belowMinWidth;
      } else if (gridTemplateColumnsCachedBreakpoints.current.scrollable.aboveEqualToMinWidth && fractionalWidth >= SCROLLABLE_CELL_MIN_WIDTH) {
        scrollableValue = gridTemplateColumnsCachedBreakpoints.current.scrollable.aboveEqualToMinWidth;
      } else {
        // if the fr width for the grid-template-columns is less than the scrollable cell min width,
        // it will return a value forces the table into a scrollable mode
        // otherwise, it will return a grid-template-columns value that applies flex values to the columns
        scrollableValue = calculateGridTemplateColumns(tableData.tables.scrollable.widths, false, 'scrollable', fractionalWidth);

        // save the calculated value for this breakpoint
        if (fractionalWidth < SCROLLABLE_CELL_MIN_WIDTH) {
          gridTemplateColumnsCachedBreakpoints.current.scrollable.belowMinWidth = scrollableValue;
        } else if (fractionalWidth >= SCROLLABLE_CELL_MIN_WIDTH) {
          gridTemplateColumnsCachedBreakpoints.current.scrollable.aboveEqualToMinWidth = scrollableValue;
        }
      }

      let leftValue = '';
      if (gridTemplateColumnsCachedBreakpoints.current.left) {
        leftValue = gridTemplateColumnsCachedBreakpoints.current.left;
      } else {
        leftValue = calculateGridTemplateColumns(tableData.tables.left.widths, !!tableData.expandableRender, 'left', fractionalWidth);
        gridTemplateColumnsCachedBreakpoints.current.left = leftValue;
      }

      let rightValue = '';
      if (gridTemplateColumnsCachedBreakpoints.current.right) {
        rightValue = gridTemplateColumnsCachedBreakpoints.current.right;
      } else {
        rightValue = calculateGridTemplateColumns(tableData.tables.right.widths, false, 'right', fractionalWidth);
        gridTemplateColumnsCachedBreakpoints.current.right = rightValue;
      }

      setTableData((prev) => {
        if (
          leftValue !== prev.tables.left.gridTemplate ||
          scrollableValue !== prev.tables.scrollable.gridTemplate ||
          rightValue !== prev.tables.right.gridTemplate
        ) {
          return {
            ...prev,
            tables: {
              left: { ...prev.tables.left, gridTemplate: leftValue },
              scrollable: { ...prev.tables.scrollable, gridTemplate: scrollableValue },
              right: { ...prev.tables.right, gridTemplate: rightValue }
            }
          };
        }
        return prev;
      });
    }
  };

  const handleSortColumn = (newSortId: string) => {
    let sortOrder = SortOrder.Ascending;
    if (newSortId !== id) {
      sortOrder = SortOrder.Ascending;
    } else {
      sortOrder = getNextSortOrder(order) as 'asc' | 'desc';
    }
    dispatch(setSorting({ id: String(newSortId), order: sortOrder as 'asc' | 'desc' }));
  };

  const calculateScrollPosition = (ref: RefObject<any>): ScrollPosition => {
    // identify where the scroll is at to determine overlays for scrolled-depth illusion
    if (!ref.current) return null;

    const { scrollLeft, scrollWidth, clientWidth } = ref.current;

    if (scrollWidth <= clientWidth) {
      return null;
    } else if (Math.floor(scrollLeft) === 0) {
      return 'start';
    } else if (Math.ceil(scrollLeft) + clientWidth >= scrollWidth) {
      return 'end';
    } else {
      return 'middle';
    }
  };

  const handleToggleRowExpansion = (id: number, index: number) => {
    const newExpandedRows = [...expansionState];

    if (newExpandedRows[index].drawerRef.current) {
      const newExpandValue = !expandedRows.includes(id);
      newExpandedRows[index] = {
        ...newExpandedRows[index],
        currentDrawerHeight: newExpandValue ? (newExpandedRows[index].drawerRef.current?.clientHeight as number) : 0
      };

      setExpansionState(newExpandedRows);
      if (expandedRows.includes(id)) {
        setExpandedRows((prev) => prev.filter((rowId) => rowId !== id));
      } else {
        setExpandedRows((prev) => [...prev, id]);
      }
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // always change the current page back to 1, since we're not sure if the total number of pages will exceed 1 page
    dispatch(setCurrentPage(1));
    dispatch(setPageSize(newPageSize));
    dispatch(collapseAllRows({ tableKey } as any));
    dispatch(updatePageSize(newPageSize));
    window.scrollTo(0, 0);
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
    dispatch(collapseAllRows({ tableKey } as any));
    window.scrollTo(0, 0);
  };

  const handleClearFilters = () => dispatch(clearFilters(undefined));

  const updateScrollPosition = throttle(() => {
    const newScrollEnd = calculateScrollPosition(scrollableRef);
    if (newScrollEnd !== scrollPosition) {
      setScrollPosition(newScrollEnd);
    }
  }, 100);

  const onGridClick = (id: string) => {
    history.push(`/ingest-management/${id}`);
  };

  useEffect(() => {
    if (!(tableSettings as any).disableGetData) dispatch(getData() as any);
  }, [tableSettings, tableParams.status_filter]);

  useEffect(() => {
    updateGridTemplateColumns();

    setExpansionState((prev) => {
      return data.map((item: any) => {
        const isExpanded = expandedRows.includes(item.id);
        if (!isExpanded) {
          return { id: item.id, currentDrawerHeight: 0, drawerRef: createRef<HTMLDivElement>() };
        }

        return {
          id: item.id,
          currentDrawerHeight: prev.find((prevItem) => prevItem.id === item.id)?.currentDrawerHeight || 0,
          drawerRef: createRef<HTMLDivElement>()
        };
      });
    });
  }, [data]);

  useEffect(() => {
    const handleResize = () => {
      // close all expanded rows if the window is resized (drawer width doesn't change with window resize)
      if (expandedRows.length > 0) {
        setExpandedRows([]);
        setExpansionState((prev) => prev.map((row) => ({ ...row, currentDrawerHeight: 0 })));
      }

      updateGridTemplateColumns();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [expandedRows]);

  useEffect(() => {
    const scrollableElement = scrollableRef.current;
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', updateScrollPosition, { passive: true });
      scrollableElement.addEventListener('touchstart', updateScrollPosition, { passive: true });

      return () => {
        scrollableElement.removeEventListener('scroll', updateScrollPosition);
        scrollableElement.removeEventListener('touchstart', updateScrollPosition);
      };
    }
  }, [scrollableRef.current]);

  useResizeObserver({
    ref: scrollableRef,
    onResize: updateScrollPosition
  });

  useEffect(() => {
    if (preferencesGridViewEnabled && !clientEnableGridViewToggle) {
      dispatch(savePreferences({ fileIngest: { grid: clientEnableGridViewToggle } }));
    }
  }, []);

  return (
    <Container>
      <ErrorBoundary FallbackComponent={() => ErrorFallback({ error: 'There was an error.' })} resetKeys={[data]}>
        <Wrapper ref={tableContainerRef}>
          {(!data?.length || !cols.length) && !loading ? (
            <TableNoResults handleClearFilters={handleClearFilters} isClearable={isFiltered && !simplified} />
          ) : (
            <>
              {preferencesGridViewEnabled && clientGridItemRender ? (
                <Grid container style={{ width: '100%' }}>
                  {data.map((row: any) => {
                    return clientGridItemRender(row, `row-${row.id}`, onGridClick);
                  })}
                </Grid>
              ) : (
                <TableWrapper>
                  <StyledLoadingBar loading={loading} />
                  {Object.keys(tableData.tables).map((k) => {
                    const position = k as keyof TableData['tables'];
                    return (
                      <StyledTable
                        key={position}
                        position={position}
                        ref={position === 'scrollable' ? scrollableRef : position === 'right' ? rightTableRef : undefined}
                        expandable={+!!tableData.expandableRender}
                        loading={+loading}
                      >
                        <Fade in={tableData.tables[position].gridTemplate !== ''} timeout={DRAWER_ANIMATION_TIMING}>
                          <div>
                            <StyledHeader
                              animationTime={DRAWER_ANIMATION_TIMING.toString()}
                              scrollPosition={scrollPosition}
                              gridTemplateColumnsCalculation={tableData.tables[position].gridTemplate}
                              position={position}
                            >
                              {tableData.tables[position].headers.map((header, index) => {
                                return (
                                  <Cell header key={`header-${index}`} isExpandable={header?.id === 'expandable'}>
                                    {header?.label}
                                    {!!header?.tooltip && (
                                      <TooltipWrapped title={header?.tooltip.title} placement={header?.tooltip.placement} arrow>
                                        <TooltipIcon icon="info" />
                                      </TooltipWrapped>
                                    )}
                                    {!header?.disableSort && header?.label && (
                                      <SortIcon
                                        icon={order === SortOrder.Ascending ? 'up' : 'down'}
                                        active={+!!(id === (header?.sortField || header?.id) && order)}
                                        height={15}
                                        onClick={() => handleSortColumn(header?.sortField || header?.id)}
                                      />
                                    )}
                                  </Cell>
                                );
                              })}
                            </StyledHeader>
                            <StyledTableBody>
                              {data.map((row: any, index: number) => {
                                const rowExpansionState = expansionState[index];
                                const isExpanded = expandedRows.includes(row.id);
                                return (
                                  <div style={{ position: 'relative' }} key={`row-${index}`}>
                                    <TableRow
                                      index={index}
                                      row={row}
                                      position={position}
                                      headers={tableData.tables[position].headers}
                                      gridTemplateColumns={tableData.tables[position].gridTemplate}
                                      onToggle={tableData.expandableRender ? () => handleToggleRowExpansion(Number(row.id), index) : undefined}
                                      onRowClick={!tableData.expandableRender ? onRowClick : undefined}
                                      currentDrawerHeight={tableData.expandableRender ? rowExpansionState?.currentDrawerHeight : undefined}
                                      animationTime={DRAWER_ANIMATION_TIMING.toString()}
                                      scrollPosition={scrollPosition}
                                      expanded={isExpanded}
                                      scrollableCellMinWidth={SCROLLABLE_CELL_MIN_WIDTH}
                                      frozenRightWidth={rightTableRef.current?.clientWidth}
                                    />
                                    {rowExpansionState && rightTableRef.current && tableData.expandableRender && position === 'left' && (
                                      <Drawer
                                        width={tableContainerRef.current ? tableContainerRef.current?.clientWidth : 0}
                                        contentHeight={rowExpansionState?.currentDrawerHeight}
                                        animationTime={isExpanded ? DRAWER_ANIMATION_TIMING.toString() : '100'}
                                      >
                                        <DrawerContent expanded={+isExpanded} ref={rowExpansionState.drawerRef}>
                                          <LeftBar />
                                          {tableData.expandableRender(row, rightTableRef.current.clientWidth)}
                                        </DrawerContent>
                                      </Drawer>
                                    )}
                                  </div>
                                );
                              })}
                            </StyledTableBody>
                          </div>
                        </Fade>
                      </StyledTable>
                    );
                  })}
                </TableWrapper>
              )}
              {!loading && !!data?.length && (
                <TableFooter container>
                  <Grid item>
                    <TableCounts
                      currentPage={currentPage}
                      pageSize={pagination!.page_size || data.length}
                      totalCount={pagination!.total || data.length}
                      tableKey={tableKey as any}
                    />
                  </Grid>
                  {pagination!.total > pagination!.page_size && (
                    <PaginationWrapper item>
                      <TablePagination
                        currentPage={currentPage}
                        pageSize={pagination!.page_size}
                        totalCount={pagination!.total}
                        onPageChange={handlePageChange}
                      />
                    </PaginationWrapper>
                  )}
                  {pagination!.page_size && (
                    <PageSizeWrapper item>
                      <TablePageSize currentPageSize={pagination!.page_size} onPageSizeChange={handlePageSizeChange} />
                    </PageSizeWrapper>
                  )}
                </TableFooter>
              )}
            </>
          )}
        </Wrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default PolarisTableContainer;

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const StyledTable = styled.div<{ position: keyof TableData['tables']; expandable: number; loading: number }>`
  border-collapse: collapse;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};

  .table-row:hover .cell {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
  ${({ theme, position, expandable }) => {
    return (
      position === 'left' &&
      !expandable &&
      `
      .cell:first-of-type {
        padding-left: ${theme.spacing(2)};
      }
    `
    );
  }}

  ${({ theme, position }) =>
    position === 'scrollable' &&
    `
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    flex-grow: 2;

    .cell:first-of-type {
    padding-left: ${theme.spacing(1)};
    }

    .cell:last-of-type {
    padding-right: ${theme.spacing(1)};
    }
  `};
`;

const StyledHeader = styled.div<{
  gridTemplateColumnsCalculation: string;
  position: keyof TableData['tables'];
  scrollPosition: ScrollPosition;
  animationTime: string;
}>`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumnsCalculation }) => gridTemplateColumnsCalculation};
  position: relative;
  min-height: 15px;
  padding-bottom: 5px;

  .cell > div {
    opacity: 0.5;
  }

  ${({ theme, position, scrollPosition, animationTime }) =>
    position === 'left' &&
    `
    .cell:last-of-type::before {
        content: '';
        position: absolute;
        top: 1px;
        right: -39px;
        height: 100%;
        width: 40px;
        z-index: 3;
        opacity: ${scrollPosition && scrollPosition !== 'start' ? 1 : 0};
        transition: opacity ${animationTime}ms ease;
        background: linear-gradient(to right, ${theme.palette.background.default} 10%, transparent 100%);
      }
  `}

  ${({ theme, position, scrollPosition, animationTime }) =>
    position === 'right' &&
    `
    .cell:first-of-type::before {
        content: '';
        position: absolute;
        top: 1px;
        left: -39px;
        height: 100%;
        width: 40px;
        z-index: 3;
        opacity: ${scrollPosition && scrollPosition !== 'end' ? 1 : 0};
        transition: opacity ${animationTime}ms ease;
        background: linear-gradient(to left, ${theme.palette.background.default} 10%, transparent 100%);
      }
  `}

  .cell {
    ${({ position }) =>
      position === 'scrollable' &&
      `
      min-width: 110px;
    `}
  }
`;

const StyledTableBody = styled.div``;

const PaginationWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;

const PageSizeWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 15px;
`;

const Drawer = styled.div<{ contentHeight: number | null; animationTime: string; width: number }>`
  position: absolute;
  top: ${ROW_HEIGHT}px;
  left: 0;
  overflow: hidden;
  transition: ${({ animationTime }) => `height ${animationTime}ms ease, border-radius ${animationTime}ms ease`};
  height: ${({ contentHeight }) => (contentHeight ? contentHeight + 1 : 0)}px;
  width: ${({ width }) => width}px;
`;

const DrawerContent = styled.div<{ expanded: number }>`
  position: relative;
  padding: ${({ theme }) => theme.spacing(2)};
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.05)};
  border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
  border-top: none;
  border-radius: ${({ expanded, theme }) => (expanded ? `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px` : '0')};
  overflow: hidden;
`;

const LeftBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.primary[theme.palette.mode]};
  z-index: 1;
`;

const TooltipWrapped = styled((props: any) => <Tooltip classes={{ popper: props.className }} {...props} />)`
  & .MuiTooltip-tooltip {
    line-height: 1.25rem;
    max-width: 200px;
    text-align: center;
  }
`;

const TooltipIcon = styled(PolarisIcon)`
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  margin-top: -2px;
  width: 12px;
  opacity: 0.8;
`;

const SortIcon = styled(PolarisIcon)<{ active: number }>`
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  opacity: ${({ active }) => (active ? 1 : 0)};
  color: ${({ theme }) => theme.palette.primary.main};
  transition: ease opacity 500ms;

  &:hover {
    cursor: pointer;
  }
`;

const StyledLoadingBar = styled(LoadingBar)`
  position: absolute;
`;
